import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import SEO from '@/components/SEO';

// Sends via /mailer/contact-handler.php (server-side SMTP mailer — see public/mailer/config.php)
const CONTACT_ENDPOINT = '/mailer/contact-handler.php';

// Get a site key at https://dash.cloudflare.com → Turnstile → Add site (domain: hng.world)
const TURNSTILE_SITE_KEY = '0x4AAAAAAEKLY_-kK9UJ5Jvk';

const ContactPage = () => {
  const { locale } = useParams();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '', website: '' });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorReason, setErrorReason] = useState(''); // 'captcha' | 'consent' | 'server'
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Explicit render (rather than relying on api.js's implicit data-sitekey auto-scan,
  // which is unreliable when the script is injected dynamically after page load).
  useEffect(() => {
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    let script = document.querySelector('script[data-turnstile]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-turnstile', 'true');
      document.body.appendChild(script);
    }
    script.addEventListener('load', renderWidget);
    return () => script.removeEventListener('load', renderWidget);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setStatus('error');
      setErrorReason('consent');
      return;
    }
    if (!turnstileToken) {
      setStatus('error');
      setErrorReason('captcha');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
          consent: true,
          consentTimestamp: new Date().toISOString(),
        }),
      });
      const result = await response.json().catch(() => null);
      if (response.ok && result?.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '', website: '' });
        setConsent(false);
      } else {
        setStatus('error');
        setErrorReason('server');
        setTurnstileToken('');
        if (widgetIdRef.current !== null) window.turnstile?.reset?.(widgetIdRef.current);
      }
    } catch {
      setStatus('error');
      setErrorReason('server');
      setTurnstileToken('');
      if (widgetIdRef.current !== null) window.turnstile?.reset?.(widgetIdRef.current);
    }
  };

  const offices = [
    { city: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱', address: 'De Entree 232, 1101EE, Amsterdam', email: 'netherlands@hng.world', phone: '+91 73856 90862' },
    { city: 'Nashik', country: 'India', flag: '🇮🇳', address: 'Vinayak Apartment, Ground Floor, Tidke Colony, Nashik, Maharashtra 422002', email: 'india@hng.world', phone: '+91 73856 90862' },
    { city: 'Pune', country: 'India', flag: '🇮🇳', address: 'Sr. no.32/1A/30 to 38/54, Westport, Pan Card Club Road, Baner, Pune, Maharashtra 411045', email: 'india@hng.world', phone: '+91 73856 90862' },
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Connect with H&G advisors across India and the Netherlands. Schedule a consultation for pharma engineering, sustainability, finance, capital markets, or M&A advisory."
        path="/contact"
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-playfair text-4xl md:text-5xl text-navy mb-4">
              Let&rsquo;s Build Something Exceptional
            </h1>
            <p className="text-lg text-mid-grey">
              Our advisors are available across our global offices. We&rsquo;ll connect you with the right team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Offices + Embed Slot */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left — Offices */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-playfair text-2xl text-navy mb-8">Global Offices</h2>
              <div className="space-y-6">
                {offices.map((office) => (
                  <div
                    key={office.city}
                    className="office-card bg-light-grey p-6 rounded-xl border-2 border-transparent"
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{office.flag}</span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-navy">
                          {office.city}, {office.country}
                        </h3>
                        <p className="text-mid-grey text-sm mt-1 flex items-center">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                          {office.address}
                        </p>
                        <p className="text-mid-grey text-sm mt-1 flex items-center">
                          <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                          {office.email}
                        </p>
                        <p className="text-mid-grey text-sm mt-1 flex items-center">
                          <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                          {office.phone}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — CRM Embed Slot */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-playfair text-2xl text-navy mb-6">Send an Enquiry</h2>

              {status === 'success' ? (
                <div
                  data-testid="enquiry-form-success"
                  className="bg-light-grey rounded-2xl p-10 text-center min-h-[480px] flex flex-col items-center justify-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-teal mb-4" />
                  <h3 className="font-playfair text-xl text-navy mb-2">Thank You</h3>
                  <p className="text-mid-grey text-sm max-w-md">
                    Your enquiry has been sent. One of our advisors will get back to you shortly.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="enquiry-form"
                  className="bg-light-grey rounded-2xl p-8 space-y-4"
                >
                  {/* Honeypot — hidden from real users, bots tend to fill every field */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    className="absolute -left-[9999px] w-px h-px opacity-0"
                    aria-hidden="true"
                  />
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-navy mb-2">Full Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input w-full"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-navy mb-2">Email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-navy mb-2">Phone</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input w-full"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-navy mb-2">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-navy mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input w-full resize-none"
                      placeholder="Tell us about your project or enquiry..."
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      required
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-teal flex-shrink-0"
                    />
                    <label htmlFor="consent" className="text-mid-grey text-sm leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to={`/${locale}/privacy-policy`} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">
                        Privacy Policy
                      </Link>
                      , and I consent to H&amp;G collecting and processing my personal data solely to respond to
                      this enquiry, in accordance with India&rsquo;s Digital Personal Data Protection Act, 2023
                      and, where applicable, the EU/UK General Data Protection Regulation.
                    </label>
                  </div>

                  <div ref={turnstileRef} />

                  {status === 'error' && (
                    <p className="text-red-600 text-sm">
                      {errorReason === 'consent'
                        ? 'Please agree to the Privacy Policy before sending.'
                        : errorReason === 'captcha'
                        ? 'Please complete the verification check above before sending.'
                        : 'Something went wrong sending your message. Please try again, or email us directly.'}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="btn-primary w-full inline-flex items-center justify-center disabled:opacity-60"
                  >
                    {status === 'submitting' ? 'Sending...' : (
                      <>Send Enquiry <Send className="w-4 h-4 ml-2" /></>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
